import React, { useEffect, useMemo, useState } from 'react';
import Podium from '../Podium';
import { QnaPanel } from '@/components/QnaPanel';
import { generateCSV, downloadCSV, CSVRecord } from '../utils';
import { useParams, useNavigate } from 'react-router-dom';
import { Quiz } from '../types';
import {
  onSnapshot,
  doc,
  collection,
  setDoc,
  serverTimestamp,
  getDocs,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { db, ensureAuth } from "@/lib/firebase";
import { getQuizRef } from '@/data/quizzes';
import { log } from '../utils/logger';
import { handleError } from '../utils/errorHandler';
import { useToast } from '../components/Toaster';
import { useAuth } from '../context/AuthContext';
import { subscribeControl, type PresenterControlState } from '@/services/presenterControl';
import {
  subscribePresenterEnabled,
  getPresenterEnabledFromCache,
} from '@/services/adminSettings';
import { presenterEnabledAtStartup } from '@/config/features';

type QuizStatus = 'draft' | 'in_progress' | 'ended';

export default function JoinQuizRoom() {
  const { id } = useParams<{ id: string }>();
  const quizId = id;
  const navigate = useNavigate();
  const [status, setStatus] = useState<QuizStatus | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [lastAnsweredIndex, setLastAnsweredIndex] = useState<number | null>(null);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [scores, setScores] = useState<{ id: string; name: string; score: number }[]>([]);
  const [participants, setParticipants] = useState<{ id: string; name: string }[]>([]);
  const { show } = useToast();
  const { user, authReady } = useAuth();
  const [control, setControl] = useState<PresenterControlState | null>(null);
  const [presenterEnabled, setPresenterLocal] = useState<boolean>(presenterEnabledAtStartup());
  useEffect(() => {
    const unsub = subscribePresenterEnabled(setPresenterLocal);
    setPresenterLocal(getPresenterEnabledFromCache() ?? presenterEnabledAtStartup());
    return () => unsub();
  }, []);

  // Removed automatic anonymous sign-in - users should explicitly sign in if needed
  // useEffect(() => {
  //   // silently sign in anonymous users so incognito can read quiz + submit
  //   ensureAuth().catch(() => {
  //     /* noop – UI will still load; rules gate reads */
  //   });
  // }, []);

  const userId = user?.uid || '';

  const name = useMemo(() => {
    return localStorage.getItem('participantName') || userId;
  }, [userId]);

  // Log when participant leaves (component unmounts)
  useEffect(() => {
    return () => {
      if (quizId && user?.uid) {
        log.info('participant_left', {
          message: `Participant "${name}" left quiz`,
          quizId,
          uid: user.uid,
          nickname: name
        });
      }
    };
  }, [quizId, user?.uid, name]);

  // Always subscribe to control state (even if presenter feature flag is off) so participants
  // can still react to host-driven phase changes.
  useEffect(() => {
    if (!quizId || !authReady || !user?.uid) return;
    return subscribeControl(quizId, c => {
      // Lightweight debug instrumentation (safe to leave – very low volume)
      if (c) {
        console.debug('[JoinQuizRoom] control update', {
          phase: c.phase,
          qIndex: c.qIndex,
          lock: c.lockResponses,
          timer: c.timerDuration,
        });
      } else {
        console.debug('[JoinQuizRoom] control cleared');
      }
      setControl(c);
    });
  }, [quizId, authReady, user?.uid]);

  // Determine if we should follow the control doc (host in presenter mode)
  const controlMode = !!control && control.mode === 'presenter';
  const phase = control?.phase;
  const currentIndex = controlMode ? control?.qIndex ?? 0 : quiz?.currentQuestionIndex ?? 0;
  // When in presenter-controlled mode, scoreboard displays only in explicit 'results' phase
  const showResultsForIndex = controlMode
    ? (phase === 'results' ? currentIndex : -1)
    : quiz?.showResultsForIndex ?? -1;

  // Reveal answers for participants when host switches to 'reveal' or 'results'
  const revealReady = controlMode
    ? (phase === 'reveal' || phase === 'results')
    : (lastAnsweredIndex !== null && showResultsForIndex >= lastAnsweredIndex);
  const effectiveStatus: QuizStatus | null = controlMode
    ? phase === 'lobby'
      ? 'draft'
      : phase === 'question' || phase === 'reveal' || phase === 'results'  // Keep in_progress for reveal and results
      ? 'in_progress'
      : phase === 'ended'
      ? 'ended'
      : status
    : status;

  useEffect(() => {
    setSelectedAnswer(null);
    setSubmitted(false);
    setSubmitting(false);
    setFeedback(null);
  }, [currentIndex]);

  useEffect(() => {
    // If in presenter mode and timer is running, use control timer
    if (controlMode && control?.timerDuration && control?.timerStartedAt) {
      const tick = () => {
        // Use timerStartedAt as the timer start time (stored as Date.now() number)
        // This ensures all clients are synchronized
        const serverStartTime = typeof control.timerStartedAt === 'number' 
          ? control.timerStartedAt 
          : control.timerStartedAt.toMillis?.() ?? Date.now();
        const localNow = Date.now();
        const elapsedSinceStart = Math.floor((localNow - serverStartTime) / 1000);
        const remain = Math.max((control.timerDuration as number) - elapsedSinceStart, 0);
        setTimeLeft(remain);
      };
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
    
    // Otherwise use quiz.questionStartTime for legacy mode
    if (!quiz || quiz.questionStartTime == null) return;
    const question = quiz.questions[currentIndex];
    if (!question) return;
    const limit = question.timeLimit ?? 30;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - (quiz.questionStartTime as number)) / 1000);
      const remain = Math.max(limit - elapsed, 0);
      setTimeLeft(remain);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [quiz?.questionStartTime, currentIndex, quiz, controlMode, control?.timerDuration, control?.timerStartedAt]);

  useEffect(() => {
    if (!quizId || !authReady || !user?.uid) return;
    const participantsCol = collection(getQuizRef(db, quizId), 'participants');
    const unsub = onSnapshot(
      participantsCol,
      snap => {
        const list: { id: string; name: string }[] = [];
        snap.forEach(docSnap => {
          const d = docSnap.data() as any;
          // Check for nickname, displayName, or name field (backwards compatibility)
          const participantName = d.nickname || d.displayName || d.name || docSnap.id;
          list.push({ id: docSnap.id, name: participantName });
        });
        setParticipants(list);
      },
      err => {
        console.warn('[JoinQuizRoom] participants snapshot error:', err);
      },
    );
    return () => unsub();
  }, [quizId, authReady, user?.uid]);

  useEffect(() => {
    if (!quizId || !authReady || !user?.uid) return;

    const ref = getQuizRef(db, quizId);

    const unsub = onSnapshot(
      ref,
      async snap => {
        if (!snap.exists()) {
          navigate('/join', { state: { error: 'Quiz not found or has been deleted.' } });
          return;
        }
        const data = snap.data() as Quiz;
        const q: Quiz = { ...data, id: quizId };
        setQuiz(q);
        setStatus(data.state);
      },
      err => {
        console.warn('[JoinQuizRoom] quiz snapshot error:', err);
      },
    );

    return () => {
      unsub();
    };
  }, [quizId, navigate, authReady, user?.uid]);

  const submitAnswer = async () => {
    if (!quizId || !quiz || !user) return;
    if (selectedAnswer === null || submitting || submitted) return;
    if (timeLeft <= 0 || (controlMode && control?.lockResponses)) {
      setFeedback("Time's up! You didn't answer in time.");
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    try {
      const questionIndex = currentIndex;
      const responsesCol = collection(getQuizRef(db, quizId), 'responses');
      const question = quiz.questions[questionIndex];
      const isCorrect = question.answers[selectedAnswer]?.isCorrect ?? false;
      const answerText = question.answers[selectedAnswer]?.text ?? '';
      const correctAnswerText =
        question.answers.find(a => a.isCorrect)?.text ?? '';

      // Calculate time taken in milliseconds from question start
      const questionStart = quiz.questionStartTime as number;
      const timeTaken = Date.now() - questionStart;
      const maxPoints = 1000;
      const minPoints = 600;
      const timeFactor = Math.min(1, timeTaken / 20000);
      const points = isCorrect
        ? Math.round(maxPoints - (maxPoints - minPoints) * timeFactor)
        : 0;

      await setDoc(doc(responsesCol, `${userId}_${questionIndex}`), {
        authorUid: userId,
        questionIndex,
        questionText: question.prompt,
        answerIndex: selectedAnswer,
        answerText,
        correctAnswerText,
        correct: isCorrect,
        timeTaken,
        points,
        timestamp: serverTimestamp(),
      });

      // Update cumulative score
      const scoreRef = doc(getQuizRef(db, quizId), 'scores', userId);
      const existing = await getDoc(scoreRef);
      const prev = existing.exists() ? (existing.data() as any).score || 0 : 0;
      const score = prev + points;
      await setDoc(scoreRef, { name, score, participantUid: userId }, { merge: true });

      await log.info('answer_submitted', { 
        message: `Answer submitted (${isCorrect ? 'correct' : 'incorrect'}, ${points} pts)`,
        quizId 
      });
      setSubmitted(true);
      setLastAnsweredIndex(questionIndex);
      setLastAnswerCorrect(isCorrect);
    } catch (err: any) {
      await handleError(err, { action: 'submitAnswer' }, quizId, show);
    } finally {
      setSubmitting(false);
    }
  };

  // Subscribe to scoreboard updates
  useEffect(() => {
    if (!quizId) return;
    const scoresCol = collection(getQuizRef(db, quizId), 'scores');
    const unsub = onSnapshot(
      scoresCol,
      snap => {
        const list: { id: string; name: string; score: number }[] = [];
        snap.forEach(docSnap => {
          const d = docSnap.data() as any;
          if (d.score !== undefined) {
            list.push({ id: docSnap.id, name: d.name ?? docSnap.id, score: d.score });
          }
        });
        list.sort((a, b) => b.score - a.score);
        setScores(list);
      },
      err => {
        console.warn('[JoinQuizRoom] scores snapshot error:', err);
      },
    );
    return () => unsub();
  }, [quizId]);

  // Finalize participant score when quiz ends
  useEffect(() => {
    if (status !== 'ended' || !quizId || !user) return;
    const finalize = async () => {
      const responsesQ = query(
        collection(getQuizRef(db, quizId), 'responses'),
        where('authorUid', '==', userId)
      );
      const snapshots = await getDocs(responsesQ);
      let score = 0;
      snapshots.forEach(d => {
        const data = d.data() as any;
        score += data.points ?? 0;
      });
      await setDoc(doc(getQuizRef(db, quizId), 'scores', userId), { name, score, participantUid: userId }, { merge: true });
    };
    finalize();
  }, [status, quizId, userId, name, user]);

  const downloadResults = async () => {
    if (!quizId || !quiz) return;
    const responsesQ = query(
      collection(getQuizRef(db, quizId), 'responses'),
      where('authorUid', '==', userId)
    );
    const snaps = await getDocs(responsesQ);
    const records: CSVRecord[] = [];
    snaps.forEach(d => {
      const data = d.data() as any;
      const q = quiz.questions[data.questionIndex];
      if (!q) return;
      records.push({
        participantName: name,
        question: q.prompt,
        answerGiven: q.answers[data.answerIndex]?.text ?? '',
        correctAnswer: q.answers.find(a => a.isCorrect)?.text ?? '',
        pointsAwarded: data.points ?? (data.correct ? 1 : 0),
      });
    });
    const csv = generateCSV(records);
    downloadCSV(`results_${quizId}_${userId}.csv`, csv);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-6 space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">
          {quiz?.title || 'Quiz Room'}
        </h2>
        <p className="text-xs sm:text-sm text-gray-500 mt-1 break-all">
          Quiz ID: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{quizId}</code>
        </p>
      </div>

      {/* Waiting Room - Draft Status */}
      {effectiveStatus === 'draft' && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 sm:p-8 text-center space-y-4 sm:space-y-6">
          <div className="animate-pulse">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          
          <div>
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
              Waiting for host to start the quiz...
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              Get ready! The quiz will begin shortly.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
            <p className="text-base sm:text-lg font-medium text-gray-700 mb-3 sm:mb-4">
              {participants.length} participant{participants.length === 1 ? '' : 's'} joined
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {participants.map(p => (
                <span
                  key={p.id}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-xs sm:text-sm font-medium shadow-sm"
                >
                  {p.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quiz In Progress */}
      {effectiveStatus === 'in_progress' && quiz && (
        // Hide question section when in results phase (show only scoreboard)
        controlMode && phase === 'results' ? null :
        currentIndex >= quiz.questions.length ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-8 text-center">
            <div className="text-4xl sm:text-6xl mb-4">✅</div>
            <p className="text-xl sm:text-2xl text-green-700 font-bold">Quiz Complete!</p>
            <p className="text-sm sm:text-base text-gray-600 mt-2">Waiting for final results...</p>
          </div>
        ) : currentIndex >= 0 ? (
          <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Question Header */}
            <div className="flex justify-between items-center pb-3 sm:pb-4 border-b">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700">
                Question {currentIndex + 1} of {quiz.questions.length}
              </h3>
              <div className={`text-xl sm:text-2xl font-bold ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-blue-600'}`} aria-live="polite" aria-atomic="true">
                {timeLeft}s
              </div>
            </div>

            {/* Question Text */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 sm:p-6">
              <p className="text-base sm:text-xl font-medium text-gray-800">
                {quiz.questions[currentIndex].prompt}
              </p>
            </div>

            {/* Answer Options */}
            <div className="space-y-2 sm:space-y-3">
              {quiz.questions[currentIndex].answers.map((ans, i) => {
                const isCorrect = ans.isCorrect;
                const isSelected = selectedAnswer === i;
                const showReveal = revealReady && submitted;
                
                return (
                  <label
                    key={i}
                    className={`flex items-center py-2.5 px-3 sm:py-3 sm:px-4 border-2 rounded-lg transition-all ${
                      showReveal
                        ? isCorrect
                          ? 'border-green-500 bg-green-50'
                          : isSelected
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-white'
                        : selectedAnswer === i
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    } ${
                      submitted || timeLeft <= 0 || (controlMode && control?.lockResponses)
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer active:scale-98'
                    } ${
                      !showReveal && (submitted || timeLeft <= 0 || (controlMode && control?.lockResponses))
                        ? 'opacity-60'
                        : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="answer"
                      value={i}
                      className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0"
                      disabled={submitted || timeLeft <= 0 || (controlMode && control?.lockResponses)}
                      checked={selectedAnswer === i}
                      onChange={() => setSelectedAnswer(i)}
                    />
                    <span className={`ml-2 sm:ml-3 text-base sm:text-lg break-words flex-1 ${
                      showReveal && isCorrect ? 'font-bold text-green-700' : ''
                    }`}>
                      {ans.text}
                    </span>
                    {showReveal && isCorrect && (
                      <span className="ml-2 text-green-600 text-xl">✓</span>
                    )}
                    {showReveal && !isCorrect && isSelected && (
                      <span className="ml-2 text-red-600 text-xl">✗</span>
                    )}
                  </label>
                );
              })}
            </div>

            {/* Submit Button */}
            <button
              type="button"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md active:scale-98"
              disabled={
                selectedAnswer === null ||
                submitted ||
                submitting ||
                timeLeft <= 0 ||
                (controlMode && control?.lockResponses)
              }
              onClick={submitAnswer}
            >
              {submitting
                ? 'Submitting...'
                : submitted
                  ? 'Nice! Waiting for reveal… ✅'
                  : 'Submit Answer'}
            </button>

            {/* Feedback Messages */}
            {lastAnsweredIndex === currentIndex && !revealReady && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center qq-anim-slide" aria-live="polite">
                <p className="text-sm sm:text-base text-blue-700 font-medium">
                  ⏳ Nice! Waiting for reveal…
                </p>
              </div>
            )}
            
            {lastAnsweredIndex === currentIndex && revealReady && (
              <div
                className={`rounded-lg p-3 sm:p-4 text-center font-bold text-base sm:text-lg ${
                  lastAnswerCorrect ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                {lastAnswerCorrect ? '✅ Correct!' : '❌ Incorrect.'}
              </div>
            )}
            
            {submitted && feedback && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-center">
                <p className="text-sm sm:text-base text-red-700">{feedback}</p>
              </div>
            )}
            
            {timeLeft === 0 && !submitted && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4 text-center">
                <p className="text-sm sm:text-base text-orange-700 font-medium">⏰ Time's up! You didn't answer in time.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-8 text-center">
            <div className="text-4xl sm:text-6xl mb-4">🎯</div>
            <p className="text-lg sm:text-xl text-blue-700 font-semibold">Waiting for first question...</p>
          </div>
        )
      )}

      {/* Scoreboard During Quiz */}
      {effectiveStatus === 'in_progress' &&
        scores.length > 0 &&
        showResultsForIndex >= currentIndex && (
          <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-800">🏆 Current Standings</h3>
            <Podium scores={scores} />
          </div>
        )}

      {/* Audience Q&A */}
      {quizId && (quiz as any)?.enableQna && (
        <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
          <QnaPanel quizId={quizId} mode="audience" />
        </div>
      )}

      {/* Quiz Ended - Final Results */}
      {effectiveStatus === 'ended' && (
        <div className="space-y-4 sm:space-y-6">
          {!(controlMode && control?.phase === 'results') && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 sm:p-6 text-center">
              <p className="text-xl sm:text-2xl font-bold text-purple-700">🏁 Quiz Ended</p>
            </div>
          )}
          
          {scores.length > 0 ? (
            <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="text-center">
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-800">🏆 Final Scoreboard</h3>
                <p className="text-sm sm:text-base text-gray-600">Congratulations to all participants!</p>
              </div>
              
              <Podium scores={scores} />
              
              <div className="text-center">
                <button
                  type="button"
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold text-sm sm:text-base hover:from-green-600 hover:to-emerald-600 transition shadow-md active:scale-98"
                  onClick={downloadResults}
                >
                  📥 Download Results
                </button>
              </div>
              
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                <table className="min-w-full border-collapse bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Rank</th>
                      <th className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Participant</th>
                      <th className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((s, index) => {
                      const maxScore = scores[0]?.score ?? 0;
                      const isTop = s.score === maxScore && index === 0;
                      return (
                        <tr key={s.id} className={isTop ? 'bg-yellow-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-4">
                            <span className="text-xs sm:text-sm font-semibold text-gray-700">#{index + 1}</span>
                          </td>
                          <td className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-4">
                            <span className={`text-xs sm:text-sm ${isTop ? 'font-bold text-yellow-600' : 'text-gray-700'} break-words`}>
                              {s.name} {isTop ? '👑' : ''}
                            </span>
                          </td>
                          <td className="border border-gray-200 px-3 sm:px-6 py-2 sm:py-4 text-center">
                            <span className="text-xs sm:text-sm font-semibold text-blue-600">{s.score}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-8 text-center">
              <p className="text-sm sm:text-base text-gray-600">No scores recorded.</p>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

